import { Redirect, useRouteMatch } from 'react-router-dom';
import { useMyAccountUsers, useCurrentAccountId } from '../../redux/selector';
import React, { useEffect } from 'react';
import { ACCOUNT_KEY, VENDORLINK_PREFIX_PATH } from '../../configs/AppConfig';
import { connect } from 'react-redux';
import { setCurrentAccountId, clearCurrentAccountId } from '../../redux/actions';

interface LoadCurrentAccountProps {
  setCurrentAccountId: (accountId: number) => void;
  clearCurrentAccountId: () => void;
}

interface RouteParam {
  accountId: string;
}

const LoadCurrentAccount: React.FC<React.PropsWithChildren<LoadCurrentAccountProps>> = ({
  setCurrentAccountId,
  clearCurrentAccountId,
  children,
}) => {
  const accountIdFromRoute = useAccountIdFromRoute();
  const accessibleAccountIds = useMyAccountUsers().map((it) => it.accountId);
  const desiredAccountId = determineCurrentAccountId(accountIdFromRoute, accessibleAccountIds);
  useEffect(() => {
    if (desiredAccountId) {
      setCurrentAccountId(desiredAccountId);
      saveAccountIdToCache(desiredAccountId);
    } else {
      clearCurrentAccountId();
    }
  }, [desiredAccountId, setCurrentAccountId, clearCurrentAccountId]);
  const accountIdFromRedux = useCurrentAccountId();
  if (accountIdFromRoute !== desiredAccountId) {
    return <Redirect to={`${VENDORLINK_PREFIX_PATH}/${desiredAccountId}`} />;
  }
  if (accountIdFromRoute !== accountIdFromRedux) {
    // Wait until the redux state is in-sync with the route.
    return <></>;
  }
  return <>{children}</>;
};

const determineCurrentAccountId = (
  accountIdFromUrl: number | undefined,
  accessibleAccountIds: number[]
): number | undefined => {
  if (accountIdFromUrl && accessibleAccountIds.includes(accountIdFromUrl)) {
    return accountIdFromUrl;
  }
  const accountIdFromCache = loadAccountIdFromCache();
  if (accountIdFromCache && accessibleAccountIds.includes(accountIdFromCache)) {
    return accountIdFromCache;
  }
  if (accessibleAccountIds.length > 0) {
    return accessibleAccountIds[0];
  }
  return undefined;
};

const useAccountIdFromRoute = (): number | undefined => {
  const routeMatch = useRouteMatch<RouteParam>();
  const accountIdParam = routeMatch.params.accountId;
  const accountId = parseInt(accountIdParam);
  if (!accountId) {
    // Return undefined if NaN.
    return undefined;
  }
  return accountId;
};

const loadAccountIdFromCache = () => {
  const savedAccountId = localStorage.getItem(ACCOUNT_KEY);
  if (!savedAccountId) {
    return undefined;
  }
  const accountId = parseInt(savedAccountId);
  if (!accountId) {
    // Return undefined if NaN.
    return undefined;
  }
  return accountId;
};

const saveAccountIdToCache = (accountId: number) => {
  localStorage.setItem(ACCOUNT_KEY, accountId.toString());
};

export default connect(null, { setCurrentAccountId, clearCurrentAccountId })(LoadCurrentAccount);
